import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useGameStore } from '../store/gameStore';

interface ClickUpgradeCardProps {
  title: string;
  description: string;
  icon: string;
  level: number;
  cost: number;
  onPurchase: () => void;
  onPurchaseBulk: (quantity: number) => void;
  canAfford: boolean;
  effect: string;
  formatNumber: (num: number) => string;
  upgradeType: string;
  coins: number;
  getUpgradeCost: (upgradeType: string, currentLevel: number) => number;
}

const ClickUpgradeCard: React.FC<ClickUpgradeCardProps> = ({
  title,
  description,
  icon,
  level,
  cost,
  onPurchase,
  onPurchaseBulk,
  canAfford,
  effect,
  formatNumber,
  upgradeType,
  coins,
  getUpgradeCost
}) => {
  const [purchaseMode, setPurchaseMode] = useState<1 | 10 | 100 | 'max'>('max');
  
  // Berechne Kosten basierend auf Kauf-Modus
  const calculateCosts = () => {
    let quantity = 1;
    
    if (purchaseMode === 10) quantity = 10;
    else if (purchaseMode === 100) quantity = 100;
    else if (purchaseMode === 'max') quantity = 1000; // Praktisches Maximum
    
    if (quantity <= 0) return { cost: 0, quantity: 0, actualQuantity: 0 };
    
    // Berechne Gesamtkosten
    let totalCost = 0;
    let affordableQuantity = 0;
    
    for (let i = 0; i < quantity; i++) {
      const levelCost = getUpgradeCost(upgradeType, level + i);
      if (totalCost + levelCost <= coins) {
        totalCost += levelCost;
        affordableQuantity++;
      } else {
        break;
      }
    }
    
    return { cost: totalCost, quantity, actualQuantity: affordableQuantity };
  };

  const { cost: bulkCost, quantity: requestedQuantity, actualQuantity } = calculateCosts();
  const displayCost = purchaseMode === 1 ? cost : bulkCost;
  const displayCanAfford = purchaseMode === 1 ? canAfford : (coins >= bulkCost && actualQuantity > 0);

  const handlePurchase = () => {
    if (purchaseMode === 1) {
      onPurchase();
    } else {
      onPurchaseBulk(actualQuantity);
    }
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="rounded-lg p-2 sm:p-3 border shadow-md bg-gradient-to-br from-white to-gray-50 border-gray-200"
    >
      <div className="text-center">
        <div className="text-2xl sm:text-3xl mb-1">{icon}</div>
        <h3 className="font-bold text-xs sm:text-sm mb-0.5 leading-tight">{title}</h3>
        <p className="text-xs mb-1.5 leading-tight text-gray-600">{description}</p>
        
        <div className="rounded p-1.5 mb-1.5 bg-blue-50">
          <div className="text-xs font-medium text-blue-600">Level: {level}</div>
          <div className="text-xs leading-tight text-blue-700">{effect}</div>
        </div>
        
        <div className="text-xs sm:text-sm font-bold mb-1.5 text-yellow-600">
          💰 {formatNumber(displayCost)}
        </div>
        
        <div className="space-y-1.5">
          {/* Kauf-Modus Buttons */}
          <div className="grid grid-cols-4 gap-1 mb-1.5">
            {[1, 10, 100, 'max'].map((mode) => (
              <button
                key={mode}
                onClick={() => setPurchaseMode(mode as typeof purchaseMode)}
                className={`px-1.5 py-1 text-xs rounded transition-colors min-h-[32px] ${
                  purchaseMode === mode
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {mode === 'max' ? 'MAX' : `${mode}x`}
              </button>
            ))}
          </div>
          
          {/* Kaufen Button */}
          <button
            onClick={handlePurchase}
            disabled={!displayCanAfford}
            className={`w-full py-2 px-2 rounded font-semibold text-xs transition-all duration-200 min-h-[36px] ${
              displayCanAfford
                ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-md hover:shadow-lg'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {displayCanAfford ? (
              purchaseMode === 1 ? '🛒 Kaufen' : `🛒 ${actualQuantity}x Kaufen`
            ) : '💰 Zu wenig Münzen'}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const ClickerSection: React.FC = () => {
  const {
    coins,
    clickPower,
    autoClickPower,
    upgrades,
    powerUps,
    playerLevel,
    incrementClick,
    upgradeClickPower,
    upgradeAutoClicker,
    purchaseUpgradeBulk,
    getUpgradeCost,
    addCoins,
    getClickMultiplier,
    getAutoGraberBonusFromStocks,
    formatNumber,
  } = useGameStore();

  const autoClickerInterval = useRef<NodeJS.Timeout | null>(null);

  // Auto-Clicker Effekt
  useEffect(() => {
    if ((autoClickPower || 0) > 0) {
              const interval = powerUps?.speedBoost?.isActive ? 500 : playerLevel > 5000 ? 2000 : 1000; // Langsamere Updates bei hohen Levels
      autoClickerInterval.current = setInterval(() => {
        const goldRushMultiplier = powerUps?.goldRush?.isActive ? 2 : 1;
        const finalAmount = Math.floor((autoClickPower || 0) * goldRushMultiplier);
        addCoins(finalAmount);
        
        // Chance auf Edelsteine mit Lucky Charm bei Auto-Clicks
        if (powerUps?.luckyCharm?.isActive && Math.random() < 0.05) { // Niedrigere Chance bei Auto-Clicks
          addCoins(1); // Bonus Münze statt Edelstein
        }
      }, interval);
    } else if (autoClickerInterval.current) {
      clearInterval(autoClickerInterval.current);
      autoClickerInterval.current = null;
    }

    return () => {
      if (autoClickerInterval.current) {
        clearInterval(autoClickerInterval.current);
      }
    };
  }, [autoClickPower, addCoins, powerUps?.speedBoost?.isActive, powerUps?.goldRush?.isActive, powerUps?.luckyCharm?.isActive, playerLevel]);

  const shovelCost = getUpgradeCost ? getUpgradeCost('shovel', upgrades?.shovelLevel || 1) : 10;
  const autoDiggerCost = getUpgradeCost ? getUpgradeCost('autoDigger', upgrades?.autoDiggerLevel || 0) : 50;

  return (
    <div className="card text-center p-2 sm:p-4">

      {/* Haupt-Clicker-Button */}
      <div className="mb-4 sm:mb-6">
        <div className="mt-2 sm:mt-3">
          {/* Hauptanzeige */}
          <div className="bg-gradient-to-r from-green-100 to-blue-100 p-2 sm:p-3 rounded-lg mb-2 sm:mb-3">
            <p className="text-lg sm:text-2xl font-bold text-gray-800">
              Pro Klick: +{formatNumber(Math.floor((clickPower || 1) * (getClickMultiplier ? getClickMultiplier() : 1)))} Münzen
            </p>
            <div className="text-xs sm:text-sm text-gray-600 mt-1">
              Basis: {formatNumber(clickPower || 1)} × Multiplier: {formatNumber(getClickMultiplier ? getClickMultiplier() : 1)}
              {powerUps?.goldRush?.isActive && (
                <span className="text-yellow-600 font-bold ml-2">× 2 (Gold Rush)</span>
              )}
              <br />
              <span className="text-xs text-blue-600">
                Level {playerLevel || 1} Bonus: +{formatNumber(((playerLevel || 1) - 1) * 0.1)}x Multiplier
              </span>
            </div>
          </div>

          {/* Auto-Clicker Info */}
          {(autoClickPower || 0) > 0 && (
            <div className="bg-green-50 border border-green-200 rounded p-1.5 mb-1.5">
              <p className="text-xs sm:text-sm text-green-700 font-medium">
                🤖 Auto: +{formatNumber(powerUps?.speedBoost?.isActive ? (autoClickPower || 0) * 2 : (autoClickPower || 0))}/Sek
                {powerUps?.speedBoost?.isActive && (
                  <span className="text-blue-600 font-bold ml-1">⚡ x2</span>
                )}
              </p>
              {/* Aktien-Bonus Anzeige */}
              {getAutoGraberBonusFromStocks && getAutoGraberBonusFromStocks() > 0 && (
                <p className="text-xs text-blue-600 font-medium mt-1">
                  📈 Aktien: +{formatNumber(getAutoGraberBonusFromStocks())}/Sek
                </p>
              )}
            </div>
          )}

          {/* Aktive Power-Ups */}
          <div className="flex flex-wrap gap-1 text-xs justify-center">
            {powerUps?.luckyCharm?.isActive && (
              <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full">🍀 Lucky</span>
            )}
            {powerUps?.goldRush?.isActive && (
              <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">💰 Gold</span>
            )}
            {powerUps?.speedBoost?.isActive && (
              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">⚡ Speed</span>
            )}
          </div>
        </div>
      </div>

      {/* Upgrade-Bereich */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
        {/* Axt-Upgrade */}
        <ClickUpgradeCard
          title="Bessere Axt"
          description="Verbessert deine Klick-Kraft permanent"
          icon="🪓"
          level={upgrades?.shovelLevel || 1}
          cost={shovelCost}
          effect={`+${formatNumber(playerLevel || 1)} Münzen pro Klick`}
          canAfford={(coins || 0) >= shovelCost}
          onPurchase={upgradeClickPower}
          onPurchaseBulk={(quantity) => purchaseUpgradeBulk('shovelLevel', quantity)}
          formatNumber={formatNumber}
          upgradeType="shovel"
          coins={coins || 0}
          getUpgradeCost={getUpgradeCost}
        />

        {/* Auto-Digger Upgrade */}
        <ClickUpgradeCard
          title="Auto-Gräber"
          description="Gräbt automatisch für dich"
          icon="🤖"
          level={upgrades?.autoDiggerLevel || 0}
          cost={autoDiggerCost}
          effect="+1 Münze/Sek automatisch"
          canAfford={(coins || 0) >= autoDiggerCost}
          onPurchase={upgradeAutoClicker}
          onPurchaseBulk={(quantity) => purchaseUpgradeBulk('autoDiggerLevel', quantity)}
          formatNumber={formatNumber}
          upgradeType="autoDigger"
          coins={coins || 0}
          getUpgradeCost={getUpgradeCost}
        />


      </div>


    </div>
  );
}; 